/** @jsx jsx */
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Box } from '@balance-web/box';
import { buildDataAttributes, jsx } from '@balance-web/core';
import type { WithDataAttributeProp } from '@balance-web/core';
import { IconButton } from '@balance-web/button';
import { XIcon } from '@balance-web/icon';
import { Text } from '@balance-web/text';
import { Inline } from '@balance-web/inline';

import { TONE_ICONS } from './icons';
import {
  decrementCSSVariableCounter,
  incrementCSSVariableCounter,
} from './bannerCSSVarCounter';

export const APP_BANNER_COUNT_CSS_VAR = '--app-banner-count';
export const APP_BANNER_HEIGHT = 40;

export type AppBannerProps = WithDataAttributeProp<{
  text: string;
  title?: string;
  tone: 'cautious' | 'critical' | 'informative';
  action?: ReactNode;
  onClose?: () => void;
  /**
   * When set to true, the css var APP_BANNER_COUNT_CSS_VAR will be incremented/decremented
   *  on mount/unmount so that it can be used in providing top offsets to page layout elements.
   */
  isGlobal?: boolean;
}>;

export const AppBanner = ({
  text,
  title,
  tone,
  action,
  onClose,
  isGlobal,
  data,
}: AppBannerProps) => {
  useEffect(
    function incrementAndDecrementAppBannerCountCSSVariableOnMountAndUnmount() {
      if (isGlobal) {
        incrementCSSVariableCounter({ cssVariable: APP_BANNER_COUNT_CSS_VAR });
        return () => {
          decrementCSSVariableCounter({
            cssVariable: APP_BANNER_COUNT_CSS_VAR,
          });
        };
      }
    },
    [isGlobal]
  );

  const Icon = TONE_ICONS[tone];

  const dataAttributes = buildDataAttributes(data);

  return (
    <Box
      background={tone}
      height={APP_BANNER_HEIGHT}
      width="100%"
      {...dataAttributes}
    >
      <Inline alignY="center" height="100%">
        <Inline align="center" flex="1" gap="xsmall">
          <Inline
            align="center"
            alignY="center"
            height={20}
            paddingRight="xsmall"
          >
            <Icon color={tone || 'informative'} size="small" />
          </Inline>
          {title && (
            <Text size="small" weight="semibold">
              {title}
            </Text>
          )}
          <Text size="small">{text}</Text>
          {action}
        </Inline>
        {onClose && (
          <Box paddingRight="small">
            <IconButton
              colorScheme="tertiary"
              icon={XIcon}
              label="Dismiss"
              size="small"
              variant="text"
              onClick={onClose}
            />
          </Box>
        )}
      </Inline>
    </Box>
  );
};
